import React from "react";
import { useParams } from "react-router-dom";

import { getDashboardBySlug } from "@/features/shared/navigation";
import { usePublishedDashboardsQuery } from "@/features/shared/hooks/queries";
import { useSelectedLocationId, useSelectedTimeRange } from "@/features/dashboard/store";
import {
  usePublishedLocationsQuery,
  usePublishedNavigationElementsQuery,
} from "@/features/dashboard/features/dashboard";
import { getIsAuthenticated } from "@/features/shared/features/authentication";
import { Location, PublishedDashboard, PublishedNavigationElement } from "@/lib/api";
import TimeRange from "@/types/time-range";

interface DashboardData {
  selectedDashboard: PublishedDashboard | undefined;
  selectedLocation: Location | undefined;
  selectedTimeRange: TimeRange;
  allDashboards: PublishedDashboard[] | undefined;
  navigationElements: PublishedNavigationElement[] | undefined;
}

/**
 * Hook that fetches the current dashboard and all available dashboards
 */
const useDashboardData = (): DashboardData => {
  const locationId = useSelectedLocationId();
  const selectedTimeRange = useSelectedTimeRange();
  const params = useParams();
  const authenticated = getIsAuthenticated();

  if (!authenticated) {
    return {
      selectedDashboard: undefined,
      selectedLocation: undefined,
      selectedTimeRange,
      allDashboards: [],
      navigationElements: [],
    };
  }

  const dashboardsQuery = usePublishedDashboardsQuery();
  const selectedDashboard = getDashboardBySlug(dashboardsQuery.data, params.dashboard);

  const navigationElements = usePublishedNavigationElementsQuery(selectedDashboard?.id);

  const locationsQuery = usePublishedLocationsQuery(selectedDashboard?.id);

  const selectedLocation = React.useMemo(
    () => locationsQuery.data?.find((location) => location.id === locationId),
    [locationId, locationsQuery.data],
  );

  return {
    selectedDashboard,
    selectedLocation,
    selectedTimeRange,
    allDashboards: dashboardsQuery.data,
    navigationElements: navigationElements.data,
  };
};

export default useDashboardData;
